import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { TransactionWarningActionEnum } from '../types';
import IconChecked from '../../icons/checked';
import IconUp from '../../icons/up';
import IconDown from '../../icons/down';
import IconChangeLoClock from '../../icons/change-log-clock';
import RequestBlock from './request-block';
import Select from '../../select/select';
import getWarningActionName from './get-warning-action-name';
import formatDate from '../../../helpers/format-date';
import Textarea from '../../textarea/textarea';
import ButtonPrimary from '../../button/button-primary';
import { ButtonSize } from '../../button/types';
import { PermissionEnum } from '../../../base-types';
import IconExpand from '../../icons/expand';
import { TransactionAgreementWarningTypes } from './types';

import styles from '../transaction.module.css';

export default function TransactionAgreementWarning({
  warning,
  employee,
  onApplyTransactionAction,
  applyTransactionsWarningIsLoading,
  internal,
  companyOfficials,
  recipients,
  senders,
  individualCustomer,
  recipientAccount,
  senderAccount,
  chats,
  createChatWithMessage,
  chatCreateInProgress,
  activePermissions,
  warningsReport,
  warningActionError,
  hideApproveActions,
  hideRejectActions,
}: TransactionAgreementWarningTypes) {
  const [collapsed, setCollapsed] = useState(false);
  const [action, setAction] = useState('');
  const [selectedRequest, setSelectedRequest] = useState('');
  const [requestText, setRequestText] = useState('');
  const [flagText, setFlagText] = useState('');

  const onApply = () => {
    if (onApplyTransactionAction) {
      onApplyTransactionAction({
        body: {
          action,
          comment: flagText,
          employeeId: employee?.id as string,
        },
        warningId: warning?.id as string,
      });
    }
  };

  useEffect(() => {
    if (warning?.action) {
      setAction(warning.action);
    }
  }, [warning?.action]);

  useEffect(() => {
    if (warning?.comment) {
      setFlagText(warning.comment);
    }
  }, [warning?.comment]);

  let warningIcon = <div className={styles.circle} />;

  if (warning?.action) {
    warningIcon = (
      <div className={styles.checked}>
        <IconChecked />
      </div>
    );

    if (warning.action === 'STOP') {
      warningIcon = (
        <div className={styles.declined}>
          <IconExpand />
        </div>
      );
    }
  }

  return (
    <div className={styles.warningItem}>
      <button
        type="button"
        className={styles.warningItemCollapseButton}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className={styles.buttonLeft}>
          {warningIcon}
          <div className={styles.buttonLeftValue}>
            Deadline for signing agreements overdue
          </div>
        </div>
        <div className={styles.buttonRight}>
          <div className={styles.blacklistName}>
            <div>{warning?.blacklist?.name}</div>
          </div>
          <div
            className={`${styles.buttonIconWrapper} ${styles.agreementsButtonIconWrapper}`}
          >
            {collapsed ? <IconUp /> : <IconDown />}
          </div>
        </div>
      </button>

      {collapsed && (
        <>
          {warning?.action ? (
            <div
              className={styles.warningAction}
            >{`Action: ${getWarningActionName(warning?.action)}`}</div>
          ) : (
            ''
          )}
          {activePermissions?.includes(PermissionEnum.CHANNELS_LIST) &&
            activePermissions?.includes(
              PermissionEnum.CHANNELS_CREATE_ACTION,
            ) &&
            !warningsReport && (
              <RequestBlock
                setRequestText={setRequestText}
                selectedRequest={selectedRequest}
                requestText={requestText}
                setSelectedRequest={setSelectedRequest}
                action={warning?.action}
                internal={internal}
                companyOfficials={companyOfficials}
                recipients={recipients}
                senders={senders}
                individualCustomer={individualCustomer}
                recipientAccount={recipientAccount}
                senderAccount={senderAccount}
                chats={chats}
                createChatWithMessage={createChatWithMessage}
                chatCreateInProgress={chatCreateInProgress}
              />
            )}
          {onApplyTransactionAction && !warning?.action && (
            <div className={styles.agreementsRequestBlock}>
              <div className={styles.actionWrapper}>
                <Select
                  name="actions"
                  value={action}
                  onChange={(e) => setAction(e.target.value as string)}
                  list={[
                    {
                      name: 'Pass once',
                      value: TransactionWarningActionEnum.PASS_ONCE,
                      hidden: hideApproveActions,
                    },
                    {
                      name: 'Stop',
                      value: TransactionWarningActionEnum.STOP,
                      hidden: hideRejectActions,
                    },
                  ]}
                  placeholder="Select actions"
                  selectWrapperClassName={styles.selectAction}
                  disabled={
                    Boolean(warning?.action) ||
                    applyTransactionsWarningIsLoading ||
                    (hideApproveActions && hideRejectActions)
                  }
                  fullWidth
                />
              </div>
            </div>
          )}

          {!warning?.action && !onApplyTransactionAction && (
            <div
              className={`${styles.warningActionApplied} ${styles.resolutionNotSelected}`}
            >
              Resolution not selected
            </div>
          )}
          {warning?.action &&
            warning?.employee &&
            warning?.employee?.profile?.firstName &&
            warning?.employee?.profile?.lastName && (
              <div className={styles.warningActionApplied}>
                <div className={styles.warningActionAppliedLeft}>
                  Resolved by&nbsp;
                  <span>{`${warning?.employee?.profile?.firstName} ${warning?.employee?.profile?.lastName}`}</span>
                </div>
                <span>{formatDate(warning?.updatedAt as string, true)}</span>
              </div>
            )}
          {action && (
            <Textarea
              name="flagText"
              value={warning?.action ? `«${flagText}»` : flagText}
              onChange={(e) => setFlagText(e.target.value)}
              placeholder="Enter a comment..."
              disabled={
                Boolean(warning?.action) || applyTransactionsWarningIsLoading
              }
              labelClassName={styles.flagTextareaLabel}
              className={warning?.action ? styles.flagTextareaDisabled : ''}
              borderClassName={
                warning?.action ? styles.flagTextareaBorderDisabled : ''
              }
            />
          )}
          <div
            className={`${styles.applyWarningWrapper} ${
              warning?.action ? styles.flexEnd : ''
            }`}
          >
            {!warning?.action && onApplyTransactionAction && (
              <ButtonPrimary
                text={
                  applyTransactionsWarningIsLoading
                    ? 'Loading...'
                    : 'Apply action'
                }
                className={styles.applyActionButton}
                size={ButtonSize.SMALL}
                disabled={
                  !(action && flagText) || applyTransactionsWarningIsLoading
                }
                onClick={onApply}
              />
            )}
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <NavLink
                to={`/administration/change-logs?entityType=WARNING&entityId=${warning?.id}`}
                className={styles.changeLogLink}
                target="_blank"
              >
                <div>
                  <IconChangeLoClock />
                  Change Log
                </div>
              </NavLink>
            )}
          </div>
          {warningActionError &&
            warningActionError.warningId === warning?.id && (
              <div className={styles.warningActionError}>
                {warningActionError.error}
              </div>
            )}
        </>
      )}
    </div>
  );
}
